import { HttpClient, HttpResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppStateInterface } from 'app/core/app-config/state/models';
import { RestConnectorService } from 'app/core/connector-services/rest-connector-service/rest-connector-service.abstract';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BackupTranslationInterface } from '../models/backup-translation.model';
import { CustomFieldInterface } from '../models/custom-field.model';

import { LicenseDataInterface } from '../models/license-data.model';
import { PasswordSettingsInterface } from '../models/password-settings.model';
import { SiteEmailConfigInterface } from '../models/site-email-config.model';
import { SiteSettingInterface } from '../models/site-setting.model';
import { SiteSupportedLanguageInterface } from '../models/site-supported-language.model';
import { UserSessionSettingsInterface }  from '../models/usersession-settings.model';
import { MessageBannerInterface, MessageInterface, MessageTextInterface }  from '../models/message-banner.model';
import { ErrorsSSOInterface, ErrorSSOSingleInterface } from '../models/error-message';
import { SSOStatusInterface } from '../models/active.model';
import { SSOMetadataInterface } from '../models/sso-metadata.model';

@Injectable({
  providedIn: 'root',
})

export class SiteSettingsRestConnectorService extends RestConnectorService {
  constructor(private readonly _httpClient: HttpClient,
              protected readonly _store: Store<AppStateInterface>) {
    super(_store);
  }

  public loadAllSiteSettings(): Observable<SiteSettingInterface[]> {
    return this._httpClient.get<SiteSettingInterface[]>(
      '/api/site-settings/flags',
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<SiteSettingInterface[]>) => httpResponse.body),
    );
  }

  public loadUserProps(): Observable<SSOStatusInterface> {
    return this._httpClient.get<SSOStatusInterface>(
      '/api/sso/status',
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<SSOStatusInterface>) => httpResponse.body),
    );
  }

  public loadSsoMetadata(): Observable<SSOMetadataInterface> {
    return this._httpClient.get<SSOMetadataInterface>(
      '/api/sso/saml/metadata-files',
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<SSOMetadataInterface>) => httpResponse.body),
    );
  }

  public updateSiteSetting(settingToUpdate: SiteSettingInterface): Observable<SiteSettingInterface> {
    return this._httpClient.put<SiteSettingInterface>(
    '/api/site-settings/flags',
    settingToUpdate,
    { observe: 'response' },
    )
    .pipe(
      map((httpResponse:  HttpResponse<SiteSettingInterface>) => {
        return <SiteSettingInterface> httpResponse.body;
      }),
    );
  }

  public checkSsoKey(key: string): Observable<any> {
    const httpHeaders = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this._httpClient.post<any>(
    '/api/sso/sso-key',
    {key},
    { observe: 'response',
      headers: httpHeaders
    },
    )
    .pipe(
      map((httpResponse:  HttpResponse<any>) => {
        return <any> httpResponse.body;
      }),
    );
  }

  public updateSsoKey(key: string): Observable<any> {
    const httpHeaders = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this._httpClient.put<any>(
    '/api/sso/sso-key',
    {key},
    { observe: 'response',
      headers: httpHeaders
    },
    )
    .pipe(
      map((httpResponse:  HttpResponse<any>) => {
        return <any> httpResponse.body;
      }),
    );
  }

  public updateSsoSetting(setting: any): Observable<any> {
    const httpHeaders = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this._httpClient.post<any>(
    '/api/sso/sso-flags',
    setting,
    { observe: 'response',
      headers: httpHeaders
    },
    )
    .pipe(
      map((httpResponse:  HttpResponse<any>) => {
        return <any> httpResponse.body;
      }),
    );
  }

  public updateBannerCheck(active: boolean): Observable<any> {
    const httpHeaders = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this._httpClient.post<any>(
    '/api/login-banner/active',
    active,
    { observe: 'response',
      headers: httpHeaders
    },
    )
    .pipe(
      map((httpResponse:  HttpResponse<any>) => {
        return <any> httpResponse.body;
      }),
    );
  }

  public updateBannerMessage(message: MessageInterface): Observable<any> {
    const httpHeaders = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this._httpClient.post<MessageInterface>(
    '/api/login-banner',
    message,
    { observe: 'response',
      headers: httpHeaders
    },
    )
    .pipe(
      map((httpResponse:  HttpResponse<MessageInterface>) => {
        return <MessageInterface> httpResponse.body;
      }),
    );
  }

  public loadBannerMessage(): Observable<MessageBannerInterface> {
    return this._httpClient.get<MessageBannerInterface>(
      '/api/login-banner',
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<MessageBannerInterface>) =>
      {
        return httpResponse.body
      }),
    );
  }

  public loadBannerTextMessage(): Observable<MessageTextInterface> {
    return this._httpClient.get<MessageTextInterface>(
      '/api/login-banner/msg',
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<MessageTextInterface>) =>
      {
        return httpResponse.body
      }),
    );
  }

  public loadErrorSSOMessage(): Observable<ErrorsSSOInterface[]> {
    return this._httpClient.get<ErrorsSSOInterface[]>(
      '/api/sso/error-message-content',
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<ErrorsSSOInterface[]>) =>
      {
        return httpResponse.body
      }),
    );
  }

  public updateErrorSSOMessage(errorSSOMessage: ErrorSSOSingleInterface): Observable<ErrorSSOSingleInterface> {
    return this._httpClient.post<ErrorSSOSingleInterface>(
      '/api/sso/error-message-content',
      errorSSOMessage,
      { observe: 'response' },
      )
      .pipe(
        map((httpResponse:  HttpResponse<ErrorSSOSingleInterface>) => {
          return httpResponse.body;
        }),
      );
  }

  public loadErrorSSOSingleMessage(errorId: number): Observable<ErrorSSOSingleInterface> {
    return this._httpClient.get<ErrorSSOSingleInterface>(
      `/api/sso/error-message-content/${errorId}/msg`,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<ErrorSSOSingleInterface>) =>
      {
        return httpResponse.body
      }),
    );
  }


  //// Custom Field Site Settings ////
  public loadAllCustomFields(): Observable<CustomFieldInterface[]> {
    return this._httpClient.get<CustomFieldInterface[]>(
      '/api/site-settings/custom-fields',
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<CustomFieldInterface[]>) => httpResponse.body),
    );
  }

  public updateCustomFields(customFieldData: CustomFieldInterface[]): Observable<CustomFieldInterface[]> {
    return this._httpClient.put<CustomFieldInterface[]>(
      '/api/site-settings/custom-fields',
      customFieldData,
      { observe: 'response' },
      )
      .pipe(
        map((httpResponse:  HttpResponse<CustomFieldInterface[]>) => {
          return httpResponse.body;
        }),
      );
  }

   //// Email Config Site Settings ////
  public loadAllEmailConfig(): Observable<SiteEmailConfigInterface> {
    return this._httpClient.get<SiteEmailConfigInterface>(
      '/api/site-settings/email-config',
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<SiteEmailConfigInterface>) => httpResponse.body),
    );
  }

  public updateEmailConfig(customFieldData: SiteEmailConfigInterface): Observable<SiteEmailConfigInterface> {
    return this._httpClient.put<SiteEmailConfigInterface>(
      '/api/site-settings/email-config',
      customFieldData,
      { observe: 'response' },
      )
      .pipe(
        map((httpResponse:  HttpResponse<SiteEmailConfigInterface>) => {
          return httpResponse.body;
        }),
      );
  }

  public loadSiteLicenseData(): Observable<LicenseDataInterface> {
    return this._httpClient.get<LicenseDataInterface>(
      '/api/site-settings/license',
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<LicenseDataInterface>) => httpResponse.body),
    );
  }

  public updateSiteLicense(licenseKey: string): Observable<string> {
    return this._httpClient.post<string>(
      '/api/site-settings/license',
      licenseKey,
      { observe: 'response' },
      )
      .pipe(
        map((httpResponse:  HttpResponse<string>) => {
          return httpResponse.body;
        }),
      );
  }


  public loadAllSiteSupportedLanguage(): Observable<SiteSupportedLanguageInterface[]> {
    return this._httpClient.get<SiteSupportedLanguageInterface[]>(
      '/api/site-settings/supported-languages',
      { observe: 'response' },
    )
      .pipe(
        map((httpResponse: HttpResponse<SiteSupportedLanguageInterface[]>) => httpResponse.body),
      );
  }

  public updateSiteSupportedLanguage(languageData: SiteSupportedLanguageInterface[]): Observable<SiteSupportedLanguageInterface[]> {
    return this._httpClient.put<SiteSupportedLanguageInterface[]>(
      '/api/site-settings/supported-languages',
      languageData,
      { observe: 'response' },
    )
      .pipe(
        map((httpResponse: HttpResponse<SiteSupportedLanguageInterface[]>) => {
          return httpResponse.body;
        }),
      );
  }
/* Backup language */
  public loadBackupTranslate(): Observable<BackupTranslationInterface> {
    return this._httpClient.get<BackupTranslationInterface>(
      '/api/site-settings/backuptranslationstate',
      { observe: 'response' },
    )
      .pipe(
        map((httpResponse: HttpResponse<BackupTranslationInterface>) => httpResponse.body),
      );
  }


  public updateBackupTranslation(languageData: BackupTranslationInterface[]): Observable<BackupTranslationInterface> {
    return this._httpClient.put<BackupTranslationInterface>(
      '/api/site-settings/backuptranslationstate',
      languageData,
      { observe: 'response' },
    )
      .pipe(
        map((httpResponse: HttpResponse<BackupTranslationInterface>) => {
          return httpResponse.body;
        }),
      );
  }
/* Password Settings */
  public loadPasswordSettings(userId:number): Observable<PasswordSettingsInterface> {
    return this._httpClient.get<PasswordSettingsInterface>(
      '/api/site-settings/password',
      { observe: 'response' },
    )
      .pipe(
        map((httpResponse: HttpResponse<PasswordSettingsInterface>) => httpResponse.body),
      );
  }

  public updatePasswordSettings(passwordData: PasswordSettingsInterface): Observable<PasswordSettingsInterface> {
    return this._httpClient.post<PasswordSettingsInterface>(
      '/api/site-settings/password',
      passwordData,
      { observe: 'response' },
    )
      .pipe(
        map((httpResponse: HttpResponse<PasswordSettingsInterface>) => {
          return httpResponse.body;
        }),
      );
  }
  /* User session Settings */
  public loadUserSessionSettings(userId:number): Observable<UserSessionSettingsInterface> {
    return this._httpClient.get<UserSessionSettingsInterface>(
      '/api/site-settings/usersession',
      { observe: 'response' },
    )
      .pipe(
        map((httpResponse: HttpResponse<UserSessionSettingsInterface>) => httpResponse.body),
      );
  }

  public updateUserSessionSettings(passwordData: UserSessionSettingsInterface): Observable<UserSessionSettingsInterface> {
    return this._httpClient.post<UserSessionSettingsInterface>(
      '/api/site-settings/usersession',
      passwordData,
      { observe: 'response' },
    )
      .pipe(
        map((httpResponse: HttpResponse<UserSessionSettingsInterface>) => {
          return httpResponse.body;
        }),
      );
  }

}
